import PropTypes from 'prop-types';
import { Box, LinearProgress, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

/**
 * PasswordStrength - Real-time password strength indicator
 * 
 * Displays a visual strength meter and requirements checklist
 * that updates in real-time as the user types their password.
 */
const PasswordStrength = ({ password }) => {
  // Define password requirements
  const requirements = [
    { label: 'En az 8 karakter', test: (pwd) => pwd.length >= 8 },
    { label: 'En az 1 büyük harf', test: (pwd) => /[A-Z]/.test(pwd) },
    { label: 'En az 1 rakam', test: (pwd) => /[0-9]/.test(pwd) }
  ];

  // Calculate met requirements
  const metRequirements = requirements.filter((req) => req.test(password));
  const strength = (metRequirements.length / requirements.length) * 100;

  // Determine strength color
  const getStrengthColor = () => {
    if (strength < 40) return 'error';
    if (strength < 80) return 'warning';
    return 'success';
  };

  // Get strength label
  const getStrengthLabel = () => {
    if (strength < 40) return 'Zayıf';
    if (strength < 80) return 'Orta';
    return 'Güçlü';
  };

  // Don't show if password is empty
  if (!password) {
    return null;
  }

  return (
    <Box className="mt-2">
      {/* Strength Bar */}
      <Box className="mb-2">
        <Box className="flex justify-between items-center mb-1">
          <Typography variant="caption" color="text.secondary">
            Şifre Gücü
          </Typography>
          <Typography 
            variant="caption" 
            color={getStrengthColor() + '.main'}
            sx={{ fontWeight: 600 }}
          >
            {getStrengthLabel()}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={strength} 
          color={getStrengthColor()}
          sx={{ height: 6, borderRadius: 1 }}
        />
      </Box>

      {/* Requirements Checklist */}
      <List dense disablePadding>
        {requirements.map((requirement, index) => {
          const isMet = requirement.test(password);
          return (
            <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isMet ? (
                  <CheckCircleIcon 
                    fontSize="small" 
                    sx={{ color: 'success.main' }} 
                  />
                ) : (
                  <CancelIcon 
                    fontSize="small" 
                    sx={{ color: 'error.main' }} 
                  />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={requirement.label}
                primaryTypographyProps={{
                  variant: 'caption',
                  color: isMet ? 'success.main' : 'text.secondary'
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

PasswordStrength.propTypes = {
  password: PropTypes.string.isRequired
};

export default PasswordStrength;

